import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";

const formatDate = (iso) => {
  const months = [
    "januar", "februar", "marts", "april", "maj", "juni",
    "juli", "august", "september", "oktober", "november", "december"];

  const d = new Date(iso);
  return `${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const BlogListPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-list-page">
      <SEO
        noindex
        title="Viden om Power BI, Business Central & data warehouse | Valubi"
        description="Praktiske indsigter om Business Intelligence, KPI'er, Power BI og Business Central — skrevet af Jonathan Midjord Jørgensen, founder af Valubi."
        path="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Valubi Viden",
          "url": "https://valubi.dk/blog",
          "inLanguage": "da-DK",
          "publisher": { "@id": "https://valubi.dk/#organization" }
        }} />

      <Navigation />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12 lg:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-12 lg:mb-16">

          <p className="text-teal-600 font-manrope font-semibold text-sm uppercase tracking-wider mb-4">
            Viden
          </p>
          <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-slate-900 mb-6 leading-tight">
            Praktiske indsigter om{" "}
            <span className="text-teal-600">BI og økonomi</span>
          </h1>
          <p className="text-slate-600 text-lg leading-relaxed">
            KPI'er, rapportering og data warehouse-tips fra mit daglige
            arbejde med Business Central, e-conomic og Power BI. Korte
            artikler — værd at læse for økonomichefer og direktører.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {blogPosts.map((post, index) =>
          <motion.article
            key={post.slug}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.05 }}
            data-testid={`blog-card-${post.slug}`}>

              <Link
              to={`/blog/${post.slug}`}
              className="group block h-full bg-white rounded-2xl p-6 lg:p-7 border border-slate-100 hover:border-teal-300 hover:shadow-lg transition-all">

                <div className="inline-flex items-center px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-manrope font-semibold uppercase tracking-wider mb-4">
                  {post.category}
                </div>
                <h2 className="font-manrope font-bold text-xl text-slate-900 mb-3 leading-snug group-hover:text-teal-600 transition-colors break-words hyphens-auto" lang="da">
                  {post.title}
                </h2>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 break-words" lang="da">
                  {post.excerpt}
                </p>
                <div className="flex items-center gap-4 text-slate-500 text-xs">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={14} />
                    <time dateTime={post.date}>{formatDate(post.date)}</time>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock size={14} />
                    <span>{post.readingTime}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-teal-600 font-medium text-sm mt-6 group-hover:gap-3 transition-all">
                  Læs artiklen
                  <ArrowRight size={16} />
                </div>
              </Link>
            </motion.article>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-100 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Valubi ApS — CVR 45291745
          </p>
          <Link
            to="/"
            className="text-slate-500 hover:text-teal-600 text-sm transition-colors"
            data-testid="blog-footer-home-link">

            valubi.dk
          </Link>
        </div>
      </footer>
    </div>);

};

export default BlogListPage;
